import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminTable } from "../../components/admin/AdminTable";
import { FeatureDisabledState } from "../../components/trainer/FeatureDisabledState";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useClients } from "../../hooks/useClients";
import { useTenant } from "../../hooks/useTenant";
import { useWorkspaceFeatures } from "../../hooks/useWorkspaceFeatures";
import type { ClientStatus } from "../../types/database";
import { formatDate, statusTone } from "../../lib/format";

const STATUSES: Array<ClientStatus | "all"> = [
  "all",
  "lead",
  "invited",
  "onboarding",
  "active",
  "paused",
  "archived",
];

export function TrainerClientsPage() {
  const { currentTenant } = useTenant();
  const { data: features } = useWorkspaceFeatures(currentTenant?.id);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ClientStatus | "all">("all");
  const { data, loading, error } = useClients({ tenantId: currentTenant?.id, search, status });

  return (
    <div>
      {features && !features.client_profiles_enabled ? (
        <FeatureDisabledState title="Client profiles are not enabled." />
      ) : (
      <>
      <AdminPageHeader title="Clients" description="Client invites and profile records for this workspace." />
      <div className="mb-5 flex flex-col justify-between gap-3 lg:flex-row lg:items-end">
        <div className="grid flex-1 gap-3 md:grid-cols-[minmax(240px,420px)_220px]">
          <Input label="Search clients" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or email" />
          <label className="text-sm font-medium text-ink">
            Status
            <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value as ClientStatus | "all")}>
              {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
        </div>
        <Link to="/trainer/clients/new"><Button>Invite client</Button></Link>
      </div>
      {loading ? <FullPageLoader label="Loading clients" /> : null}
      {error ? <EmptyState title="Unable to load clients.">{error}</EmptyState> : null}
      {!loading && !error ? (
        data?.length ? (
          <AdminTable headers={["Name", "Email", "Status", "Assigned trainer", "Goal", "Created", "Last activity", "Actions"]}>
            {data.map((client) => (
              <tr key={client.id}>
                <td className="px-4 py-3 font-medium text-ink">{client.full_name}</td>
                <td className="px-4 py-3">{client.email}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(client.status)}>{client.status}</Badge></td>
                <td className="px-4 py-3">{client.assigned_trainer?.full_name || client.assigned_trainer?.email || "Unassigned"}</td>
                <td className="px-4 py-3">{client.goal ?? "None set"}</td>
                <td className="px-4 py-3">{formatDate(client.created_at)}</td>
                <td className="px-4 py-3 text-graphite/50">Not tracked yet</td>
                <td className="px-4 py-3"><Link className="link" to={`/trainer/clients/${client.id}`}>Open</Link></td>
              </tr>
            ))}
          </AdminTable>
        ) : (
          <EmptyState title="No clients yet.">Invite the first client to this workspace.</EmptyState>
        )
      ) : null}
      </>
      )}
    </div>
  );
}
